import React, { useCallback, useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'
import type { Recipe } from '@library/menu-service'

import { FlexPortionEditor } from './FlexPortionEditor'
import { FlexPortionSummary } from './FlexPortionSummary'

const EDITOR_PANEL_ANIMATION_MS = 260
const EDITOR_PANEL_EASING = 'cubic-bezier(0.2, 0, 0, 1)'
const EDITOR_PANEL_MAX_HEIGHT = 420
const COLLAPSED_FOOTER_HEIGHT = 153

const EditorPanel = styled.div<{ isOpen: boolean }>(({ isOpen }) => ({
  maxHeight: `${isOpen ? EDITOR_PANEL_MAX_HEIGHT : COLLAPSED_FOOTER_HEIGHT}px`,
  opacity: isOpen ? 1 : 0,
  overflow: 'hidden',
  transform: isOpen ? 'translateY(0)' : 'translateY(4px)',
  transition: [
    `max-height ${EDITOR_PANEL_ANIMATION_MS}ms ${EDITOR_PANEL_EASING}`,
    `opacity ${EDITOR_PANEL_ANIMATION_MS}ms ${EDITOR_PANEL_EASING}`,
    `transform ${EDITOR_PANEL_ANIMATION_MS}ms ${EDITOR_PANEL_EASING}`,
  ].join(', '),

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}))

const EditorPanelContent = styled.div({
  overflow: 'hidden',
})

type FlexPortionFooterProps = {
  recipeCount: number
  numPortions: number
  portions: Recipe['portions']
  onUpdatePortions: (numPortions: number) => void
  onSelectPortions?: (numPortions: number) => void
  onRemoveRecipe: () => void
  mode?: 'summary' | 'editor'
  onCloseEditor?: () => void
  onEditPortions?: () => void
}

export const FlexPortionFooter = ({
  recipeCount,
  numPortions,
  portions,
  onUpdatePortions,
  onSelectPortions,
  onRemoveRecipe,
  mode,
  onCloseEditor,
  onEditPortions,
}: FlexPortionFooterProps) => {
  const isControlled = mode !== undefined
  const isEditorMode = mode === 'editor'
  const [isEditorMounted, setIsEditorMounted] = useState(isEditorMode)
  const [isEditorOpen, setIsEditorOpen] = useState(isEditorMode)
  const skipInitialOpenAnimationRef = useRef(isEditorMode)
  const controlledCloseInProgressRef = useRef(false)
  const animationFrameRef = useRef<number | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    if (isEditorOpen || !isEditorMounted) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setIsEditorMounted(false)
    }, EDITOR_PANEL_ANIMATION_MS)

    return () => window.clearTimeout(timeoutId)
  }, [isEditorOpen, isEditorMounted])

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current)
      }

      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  const openEditor = useCallback(() => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current)
    }

    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }

    controlledCloseInProgressRef.current = false
    setIsEditorOpen(false)
    setIsEditorMounted(true)

    animationFrameRef.current = window.requestAnimationFrame(() => {
      setIsEditorOpen(true)
      animationFrameRef.current = null
    })
  }, [])

  const closeEditor = useCallback(() => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    setIsEditorOpen(false)
    controlledCloseInProgressRef.current = isControlled

    if (onCloseEditor) {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current)
      }

      closeTimeoutRef.current = window.setTimeout(() => {
        onCloseEditor()
        closeTimeoutRef.current = null
      }, EDITOR_PANEL_ANIMATION_MS)
    }
  }, [isControlled, onCloseEditor])

  useEffect(() => {
    if (!isControlled) {
      return undefined
    }

    if (!isEditorMode) {
      controlledCloseInProgressRef.current = false

      if (isEditorMounted) {
        closeEditor()
      }

      return undefined
    }

    if (controlledCloseInProgressRef.current) {
      return undefined
    }

    if (isEditorMode) {
      if (skipInitialOpenAnimationRef.current) {
        skipInitialOpenAnimationRef.current = false

        return undefined
      }

      openEditor()
    }

    return undefined
  }, [closeEditor, isControlled, isEditorMode, isEditorMounted, openEditor])

  const handleUpdatePortions = (updatedNumPortions: number) => {
    onUpdatePortions(updatedNumPortions)
    closeEditor()
  }

  const handleRemoveRecipe = () => {
    onRemoveRecipe()
    closeEditor()
  }

  if (isEditorMounted) {
    return (
      <EditorPanel data-testid="flex-portion-footer-editor" isOpen={isEditorOpen}>
        <EditorPanelContent>
          <FlexPortionEditor
            recipeCount={recipeCount}
            numPortions={numPortions}
            portions={portions}
            onClose={closeEditor}
            onUpdatePortions={handleUpdatePortions}
            onSelectPortions={onSelectPortions}
            onRemoveRecipe={handleRemoveRecipe}
          />
        </EditorPanelContent>
      </EditorPanel>
    )
  }

  return (
    <Box data-testid="flex-portion-footer" width="100%">
      <Box
        alignItems={AlignItems.Center}
        borderBottomWidth={0.5}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        display={Display.Flex}
        justifyContent={JustifyContent.FlexEnd}
        paddingH={3}
        paddingV={1}
      >
        <FlexPortionSummary
          recipeCount={recipeCount}
          portionCount={numPortions}
          onEdit={onEditPortions ?? openEditor}
        />
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} paddingH={3} paddingV={2}>
        <Button disabled width="100%">
          Recipe Added
        </Button>
        <Space direction="vertical" size={2} />
        <Button
          colorVariant={ButtonColorVariant.Tertiary}
          onClick={handleRemoveRecipe}
          width="100%"
        >
          <Icon name="delete" variant={IconVariant.Actionable} />
          <Space direction="horizontal" size={1} />
          Remove recipe
        </Button>
      </Box>
    </Box>
  )
}
